import { parseCSV } from '@/utils/csvParser';
import { emptyNormalized, type NormalizedImport, type NormalizedTask } from '../normalizedImport';

// Generic CSV parser: best-effort column detection.
// Supports: name/title, parent, intent/description, assignee, start, end, status, dependencies, tags
export function parseGenericCSV(csvText: string, sourcePlatform = 'generic'): NormalizedImport {
  const out = emptyNormalized(sourcePlatform);
  const rows = parseCSV(csvText);
  if (rows.length < 2) {
    out.warnings.push('CSV has no data rows');
    return out;
  }
  const headers = rows[0].map((h) => h.toLowerCase().trim());
  const idx = (...names: string[]) =>
    headers.findIndex((h) => names.some((n) => h === n || h.includes(n)));

  const cName = idx('name', 'title', 'task');
  const cParent = idx('parent', 'parent task');
  const cIntent = idx('intent', 'description', 'notes', 'purpose');
  const cAssignee = idx('assignee', 'owner', 'assigned to');
  const cStart = idx('start', 'start date');
  const cEnd = idx('end', 'due', 'deadline', 'end date');
  const cStatus = idx('status', 'state');
  const cDeps = idx('depends', 'predecessor', 'dependencies');
  const cTags = idx('tags', 'labels');
  const cExternal = idx('id', 'external id', 'task id');

  const projectName = sourcePlatform === 'generic' ? 'Imported tasks' : sourcePlatform;
  const projectExternalId = `proj:${sourcePlatform}`;
  out.projects.push({ externalId: projectExternalId, name: projectName });

  const seenAssignees = new Map<string, string>();

  rows.slice(1).forEach((row, i) => {
    const name = (cName >= 0 ? row[cName] : row[0])?.trim();
    if (!name) return;
    const externalId = (cExternal >= 0 && row[cExternal]?.trim()) || `${sourcePlatform}:${i}`;

    const assigneeRaw = cAssignee >= 0 ? row[cAssignee]?.trim() : '';
    const assigneeIds: string[] = [];
    if (assigneeRaw) {
      assigneeRaw.split(/[;,]/).map((a) => a.trim()).filter(Boolean).forEach((a) => {
        if (!seenAssignees.has(a.toLowerCase())) {
          const id = `assignee:${a.toLowerCase()}`;
          seenAssignees.set(a.toLowerCase(), id);
          out.assignees.push({ externalId: id, name: a });
        }
        assigneeIds.push(seenAssignees.get(a.toLowerCase())!);
      });
    }

    const task: NormalizedTask = {
      externalId,
      parentExternalId: cParent >= 0 ? row[cParent]?.trim() || null : null,
      name,
      intent: cIntent >= 0 ? row[cIntent]?.trim() : undefined,
      assigneeExternalIds: assigneeIds.length ? assigneeIds : undefined,
      startDate: cStart >= 0 ? toIsoOrUndef(row[cStart]) : undefined,
      endDate: cEnd >= 0 ? toIsoOrUndef(row[cEnd]) : undefined,
      status: cStatus >= 0 ? row[cStatus]?.trim() : undefined,
      tags: cTags >= 0 ? row[cTags]?.split(/[;,]/).map((t) => t.trim()).filter(Boolean) : undefined,
    };
    out.tasks.push(task);

    if (cDeps >= 0) {
      row[cDeps]?.split(/[;,]/).map((d) => d.trim()).filter(Boolean).forEach((dep) => {
        out.dependencies.push({ fromExternalId: dep, toExternalId: externalId });
      });
    }
  });

  return out;
}

function toIsoOrUndef(s?: string): string | undefined {
  if (!s) return undefined;
  const d = new Date(s);
  return isNaN(d.getTime()) ? undefined : d.toISOString();
}
